import MainTable from '@Components/MainTable';
import { onPageChangeRequestMeta } from '@Components/MainTable/MainTable.types';
import NoResults from '@Components/NoResults/NoResults';
import {
	AnalyticsCardsContainer,
	EnrollmentsCard,
	EnrollmentTypeCard,
	enrollmentIcons,
	FormControlLabel,
	FormControlsWrapper,
	StyledIconButton,
	TypeInfo,
	TypeName,
	TypeValue,
} from '@Components/UI/Analytics/Analytics.styled';
import { AnalyticsTotal } from '@Components/UI/Analytics/Assets/Icons/Icons';
import RoundButton from '@Components/UI/Buttons/RoundButton';
import MultipleLinesChart from '@Components/UI/Charts/MultipleLinesChart/MultipleLinesChart';
import EdFormControl from '@Components/UI/Inputs/EdFormControl/EdFormControl';
import SelectChip from '@Components/UI/Inputs/SelectChip/SelectChip';
import DateRangePickerPopup, {
	DateRangeOption,
} from '@Components/UI/Pickers/DateRangePickerPopup/DateRangePickerPopup';
import MonthRangePicker from '@Components/UI/Pickers/MonthRangePicker/MonthRangePicker';
import YearRangePicker from '@Components/UI/Pickers/YearRangePicker/YearRangePicker';
import { WrappedTableCell } from '@Components/UI/Tables/Tables.styled';
import { TableHeader } from '@Features/generics/generics.type';
import {
	Box,
	Button,
	Checkbox,
	Icon,
	MenuItem,
	Paper,
	PaperProps,
	TableCell,
	TableRow,
} from '@mui/material';
import { deepPurple, green, orange, red, yellow } from '@mui/material/colors';
import { AnalyticsSlices } from '@Pages/ClassRooms/Constants/Classroom.constants';
import { useSnackbar } from '@Providers/useSnackbar';
import api from '@Services/api';
import {
	EnrollmentType,
	GraphSlice,
	GraphSliceType,
} from '@Services/classrooms/classrooms.res.types';
import { FlexItem, FlexLayout, Spacer } from '@Styled/utilities';
import { rgba } from 'polished';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import useStandalonePageLoader from './useStandalonePageLoader';

type Lecture = { id: number; name: string; order: number };
type StandaloneCourse = {
	id: number;
	label: string;
	tabs?: Array<{ sections?: Array<{ courses?: Lecture[] }> }>;
};

type AnalyticsData = {
	enrollment_summary?: {
		purchase_methods_total: number;
		purchase_methods: Array<{
			purchase_method: EnrollmentType;
			purchase_method_count: number;
		}>;
	};
	lectures?: any[];
	lecture_meta?: any;
	graph?: GraphSlice[];
	learning_time?: Record<string, number>;
};

type FormValues = {
	slice: GraphSliceType;
	lecture_ids: number[];
	dates: Date[];
};

const TABLE_HEADS: TableHeader[] = [
	{ displayName: 'Lecture', fieldName: 'name', canSort: false, width: '20%' },
	{ displayName: 'Started', fieldName: 'started_count', canSort: false },
	{ displayName: 'Active', fieldName: 'active_count', canSort: false },
	{ displayName: 'Expired', fieldName: 'expired_count', canSort: false },
	{ displayName: 'Completed', fieldName: 'completed_count', canSort: false },
	{ displayName: 'Average Progress', fieldName: 'average_progress', canSort: false },
];

const StandaloneCourseAnalytics: React.FC<{ course: StandaloneCourse }> = ({
	course,
}) => {
	const { displaySnackbar } = useSnackbar();
	const { withLoading } = useStandalonePageLoader();
	const [data, setData] = useState<AnalyticsData>({});
	const [dateRange, setDateRange] = useState<DateRangeOption>('last 2 weeks');
	const [dateOpen, setDateOpen] = useState(false);
	const [dateAnchor, setDateAnchor] = useState<HTMLElement>();
	const lectures = useMemo(
		() =>
			(course.tabs || []).flatMap((tab) =>
				(tab.sections || []).flatMap((section) => section.courses || [])
			),
		[course.tabs]
	);
	const defaults = useMemo<FormValues>(() => {
		const start = new Date();
		start.setDate(start.getDate() - 14);
		return { slice: 'day', lecture_ids: [], dates: [start, new Date()] };
	}, []);
	const { control, watch, setValue, reset, getValues } = useForm<FormValues>({
		defaultValues: defaults,
	});
	const selectedLectures = watch('lecture_ids');
	const selectedSlice = watch('slice');

	const load = useCallback(
		async (request: Partial<onPageChangeRequestMeta> = {}, form?: FormValues) => {
			const values = form || getValues();
			const requestFrom =
				request.startDate && !Number.isNaN(request.startDate.getTime())
					? request.startDate
					: values.dates[0];
			const requestTo =
				request.endDate && !Number.isNaN(request.endDate.getTime())
					? request.endDate
					: values.dates[1];
			try {
				await withLoading(async () => {
					const response = await api.post('standalone-courses/analytics', {
						standalone_course_id: course.id,
						page: request.activePage || 1,
						perPage: request.perPage || 10,
						query: request.query || '',
						filters: request.filters || [],
						sortBy: request.sortBy || { field: 'order', direction: 'asc' },
						from: requestFrom,
						to: requestTo,
						slice: values.slice,
						lecture_ids: values.lecture_ids,
					});
					setData(response.data.data || {});
				});
			} catch (error: any) {
				displaySnackbar(
					'error',
					error.response?.data?.message || 'Unable to load standalone Course analytics'
				);
			}
		},
		[course.id, displaySnackbar, getValues, withLoading]
	);

	useEffect(() => {
		load({ activePage: 1, perPage: 10 });
	}, [load]);

	const pickTypeColor = (type: EnrollmentType) => {
		if (type === 'admin') return red[500];
		if (type === 'wallet') return deepPurple[500];
		if (type === 'scratchcard') return orange[500];
		if (type === 'free') return yellow[600];
		return green.A700;
	};

	const changeDateRange = (type: DateRangeOption) => {
		setDateRange(type);
		if (type === 'custom') return;
		const start = new Date();
		const end = new Date();
		if (type === 'last 2 weeks') start.setDate(start.getDate() - 14);
		if (type === 'last month') start.setMonth(start.getMonth() - 1);
		if (type === 'last 3 months') start.setMonth(start.getMonth() - 2);
		setValue('dates', [start, end]);
	};

	const resetFilters = () => {
		reset(defaults);
		setDateRange('last 2 weeks');
		load({ activePage: 1, perPage: 10 }, defaults);
	};

	const removeLectureFilter = (lectureId: number) => {
		setValue(
			'lecture_ids',
			selectedLectures.filter((id) => id !== lectureId)
		);
	};

	const exportRows = () => {
		const rows = data.lectures || [];
		const csv = [
			['Lecture', 'Started', 'Active', 'Expired', 'Completed', 'Average Progress'],
			...rows.map((lecture) => [
				lecture.name,
				lecture.started_count,
				lecture.active_count,
				lecture.expired_count,
				lecture.completed_count,
				`${Math.round(Number(lecture.average_progress || 0))}%`,
			]),
		]
			.map((row) => row.map((value) => `"${String(value).replaceAll('"', '""')}"`).join(','))
			.join('\n');
		const link = document.createElement('a');
		link.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
		link.download = `${course.label}-analytics.csv`;
		link.click();
		URL.revokeObjectURL(link.href);
	};

	return (
		<AnalyticsPage>
			{data.enrollment_summary && (
				<ResponsiveAnalyticsCards>
					<EnrollmentsCard elevation={10}>
						<AnalyticsTotal />
						<TypeInfo>
							<span>{data.enrollment_summary.purchase_methods_total}</span>
							<label>Enrollments</label>
						</TypeInfo>
					</EnrollmentsCard>
					{data.enrollment_summary.purchase_methods.map((method) => (
						<EnrollmentTypeCard
							key={method.purchase_method}
							color={pickTypeColor(method.purchase_method)}
						>
							<TypeInfo>
								{enrollmentIcons[method.purchase_method]}
								<TypeValue>{method.purchase_method_count}</TypeValue>
							</TypeInfo>
							<TypeName>{method.purchase_method}</TypeName>
						</EnrollmentTypeCard>
					))}
				</ResponsiveAnalyticsCards>
			)}

			<Box width="100%" display="flex" justifyContent="flex-end">
				<RoundButton
					onClick={exportRows}
					startIcon={<Icon>south</Icon>}
					variant="contained"
				>
					Export
				</RoundButton>
			</Box>

			<MainTable
				tableFilters={[]}
				RowsperPage={data.lecture_meta?.per_page || 10}
				onPageChangeRequest={load}
				totalPages={data.lecture_meta?.last_page || 1}
				total={data.lecture_meta?.total || 0}
				pageNum={data.lecture_meta?.current_page || 1}
				tableHeads={TABLE_HEADS}
				renderItems={() => {
					if (!data.lectures?.length) {
						return (
							<TableRow>
								<TableCell colSpan={TABLE_HEADS.length}>
									<NoResults />
								</TableCell>
							</TableRow>
						);
					}
					return data.lectures.map((lecture) => (
						<TableRow key={lecture.id}>
							<WrappedTableCell align="center">{lecture.name}</WrappedTableCell>
							<TableCell align="center">{lecture.started_count}</TableCell>
							<TableCell align="center">{lecture.active_count}</TableCell>
							<TableCell align="center">{lecture.expired_count}</TableCell>
							<TableCell align="center">{lecture.completed_count}</TableCell>
							<TableCell align="center">
								{Math.round(Number(lecture.average_progress || 0))}%
							</TableCell>
						</TableRow>
					));
				}}
			/>

			<FormControlsWrapper>
				<FormControlLabel>Select Lecture(s)</FormControlLabel>
				<EdFormControl
					width="12rem"
					select
					multiple
					SelectProps={{
						multiple: true,
						renderValue: () =>
							selectedLectures
								.map((id) => lectures.find((lecture) => lecture.id === id)?.name)
								.filter(Boolean)
								.join(', '),
					}}
					label=""
					name="lecture_ids"
					control={control}
				>
					{lectures.map((lecture) => (
						<MenuItem value={lecture.id} key={lecture.id}>
							<Checkbox checked={selectedLectures.includes(lecture.id)} />
							<div>{lecture.name}</div>
						</MenuItem>
					))}
				</EdFormControl>
				<Spacer mx={14} />
				<EdFormControl select name="slice" control={control}>
					{AnalyticsSlices.map((slice) => (
						<MenuItem key={slice.value} value={slice.value}>
							{slice.label}
						</MenuItem>
					))}
				</EdFormControl>
				<Spacer mx={14} />
				<StyledIconButton
					onClick={(event) => {
						setDateOpen(!dateOpen);
						setDateAnchor(event.currentTarget);
					}}
				>
					<Icon>event</Icon>
				</StyledIconButton>
				<DateRangePickerPopup
					open={dateOpen}
					anchorEl={dateAnchor}
					onClose={() => setDateOpen(false)}
					onChange={changeDateRange}
					rangeOptionValue={dateRange}
				>
					<DateRangePickerPopup.Body>
						{selectedSlice === 'month' && (
							<MonthRangePicker control={control as any} name="dates" />
						)}
						{selectedSlice === 'year' && (
							<YearRangePicker control={control as any} name="dates" />
						)}
					</DateRangePickerPopup.Body>
				</DateRangePickerPopup>
			</FormControlsWrapper>
			<FlexLayout alignItems="flex-end">
				<FlexItem flex="1">
					{selectedLectures.map((lectureId) => {
						const lecture = lectures.find((item) => item.id === lectureId);
						return (
							<SelectChip
								key={lectureId}
								onDelete={() => removeLectureFilter(lectureId)}
								label={lecture?.name}
							/>
						);
					})}
				</FlexItem>
				<FlexLayout>
					<Button onClick={resetFilters} variant="outlined">
						Reset
					</Button>
					<Spacer mx="1rem" />
					<Button onClick={() => load({ activePage: 1, perPage: 10 })} variant="contained">
						Apply
					</Button>
				</FlexLayout>
			</FlexLayout>

			<MultipleLinesChart
				sliceType={selectedSlice}
				colors={(line: GraphSlice) => lectureGraphColor(String(line.id))}
				data={data.graph || []}
			/>
			<LearningTimeCards values={data.learning_time || {}} />
		</AnalyticsPage>
	);
};

const lectureGraphColor = (value: string) => {
	const colors = ['#6C63FF', '#5AC0FC', '#FF8532', '#00CE67', '#E53935'];
	const hash = value
		.split('')
		.reduce((total, character) => total + character.charCodeAt(0), 0);
	return colors[hash % colors.length];
};

const LearningTimeCards: React.FC<{ values: Record<string, number> }> = ({
	values,
}) => {
	const colors: Record<string, string> = {
		total: '#5AC0FC',
		videos: '#6C63FF',
		documents: '#FF8532',
		tests: '#FFD037',
		webinar: '#ff3100',
		webcontent: '#36a2eb',
		assignment: '#00ce67',
	};
	const labels: Record<string, string> = {
		total: 'learning time',
		videos: 'videos',
		documents: 'documents',
		tests: 'tests',
		webinar: 'webinars',
		webcontent: 'web content',
		assignment: 'assignment',
	};
	const time = (seconds: number) => {
		if (!seconds) return '0';
		const minutes = Math.floor(seconds / 60);
		const hours = Math.floor(minutes / 60);
		const days = Math.floor(hours / 24);
		if (days) return `${days}d ${hours % 24}h`;
		if (hours) return `${hours}h ${minutes % 60}m`;
		return `${minutes}m`;
	};
	return (
		<LearningCardsGrid>
			{Object.keys(colors).map((key) => (
				<LearningCard key={key} $color={colors[key]}>
					<LearningTypeInfo>
						<LearningTypeValue color={colors[key]}>{labels[key]}</LearningTypeValue>
					</LearningTypeInfo>
					<LearningTypeName>{time(Number(values[key] || 0))}</LearningTypeName>
				</LearningCard>
			))}
		</LearningCardsGrid>
	);
};

const AnalyticsPage = styled.div`
	width: 100%;
	max-width: 100%;
	min-width: 0;
`;

const ResponsiveAnalyticsCards = styled(AnalyticsCardsContainer)`
	width: 100%;
	flex-wrap: wrap;
	gap: 1rem;

	> * {
		margin: 0;
	}
`;

const LearningCardsGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(10.5rem, 1fr));
	gap: 1rem;
	width: 100%;
	padding-bottom: 5rem;
`;

const LearningPaper = styled(Paper).attrs<PaperProps>({
	elevation: 0,
})`
	padding: 1.25rem;
`;

const LearningTypeValue = styled.div<{ color?: string }>`
	color: ${(props) => props.color};
	font-size: 1.063rem;
	font-weight: 600;
	text-transform: capitalize;
`;

const LearningTypeInfo = styled.div`
	display: flex;
	justify-content: space-between;
`;

const LearningTypeName = styled.div`
	font-size: 1.063rem;
	font-weight: 600;
	color: #251b33;
	text-align: end;
`;

const LearningCard = styled(LearningPaper)<{ $color: string }>`
	display: flex;
	min-width: 0;
	flex-direction: column;
	justify-content: space-between;
	gap: 0.5rem;
	margin: 0;
	background: ${(props) => rgba(props.$color, 0.1)};
	color: ${(props) => props.$color};
	max-height: 7.75rem;
`;

export default StandaloneCourseAnalytics;
